import { inject } from 'vue'
import { useUserSession } from '/@src/stores/userSession'
import * as apisPortal from '/@src/client-portal/api'
import {
  OsobaOrganizacieZaradenieZaradenieEnum,
  ZoPPMPriloha,
  ZoPPMPodmienka,
} from '../client-portal'
import { isEntityActive } from './useSubjekt'
import { ZoppmSchemeTransformer } from '../utils/zoppm'
import { useRozpocetStore } from '/@src/stores/rozpocet'
import { merge } from '../utils/object'
import { FormActions } from 'vee-validate'
import deepmerge from 'deepmerge'

export const useZoppm = () => {
  const userSession = useUserSession()
  const getSubjektApi = new apisPortal.SubjektyApi()
  const getOrgApi = new apisPortal.OrganizacieApi()
  const getZoppmApi = new apisPortal.ZoppmDraftApi()
  const setValues = inject<FormActions<any>['setValues']>('setValues')

  const roz = useRozpocetStore()

  const { osoba: sessionOsoba } = userSession?.user?.pouzivatel || {}
  const { subjekt: sessionSubjekt } = userSession?.user || {}

  const osoba = {
    osobaRef: {
      osobaId: sessionOsoba?.osobaId,
      menoUplne: sessionOsoba?.menoUplne,
      datumNarodenia: sessionOsoba?.datumNarodenia,
      adresaUplna: sessionOsoba?.adresaUplna,
      cisloVerzie: sessionOsoba?.cisloVerzie,
    },
  }

  const subjektRef = {
    subjektId: sessionSubjekt?.subjektId,
    nazov: sessionSubjekt?.nazov,
    ico: sessionSubjekt?.ico,
    dic: sessionSubjekt?.dic,
    cisloVerzie: sessionSubjekt?.cisloVerzie,
  }

  const getStatutari = async () => {
    const statutari = sessionSubjekt?.organizaciaId
      ? await getOrgApi
          .portalGetOrganizaciaOsobaListOrganizacie(sessionSubjekt.organizaciaId)
          .then((t) =>
            t.data?.filter(
              ({ zaradenia, meta }) =>
                isEntityActive({ meta }) &&
                zaradenia?.some(
                  ({ zaradenie, meta }) =>
                    isEntityActive({ meta }) &&
                    zaradenie === OsobaOrganizacieZaradenieZaradenieEnum['Statutar']
                )
            )
          )
      : []

    return {
      osoby: statutari?.map((statutar) => ({
        osobaRef: {
          osobaId: statutar.osoba?.osobaId,
          menoUplne: statutar.osoba?.menoUplne,
          datumNarodenia: statutar.osoba?.datumNarodenia,
          adresaUplna: statutar.osoba?.adresaUplna,
        },
      })),
    }
  }

  const getKonecniUzivateliaVyhod = async () => {
    const konecniUzivateliaVyhod = sessionSubjekt?.organizaciaId
      ? await getOrgApi
          .portalGetOrganizaciaOsobaListOrganizacie(sessionSubjekt.organizaciaId)
          .then((t) =>
            t.data?.filter(
              ({ zaradenia, ...osoba }) =>
                isEntityActive(osoba) &&
                zaradenia?.some(
                  ({ zaradenie, meta }) =>
                    isEntityActive({ meta }) &&
                    zaradenie ===
                      OsobaOrganizacieZaradenieZaradenieEnum['KonecnyUzivatelVyhod']
                )
            )
          )
      : []

    return {
      osoby: konecniUzivateliaVyhod?.map((uzivatel) => ({
        osobaRef: {
          osobaId: uzivatel.osoba?.osobaId,
          menoUplne: uzivatel.osoba?.menoUplne,
          datumNarodenia: uzivatel.osoba?.datumNarodenia,
          adresaUplna: uzivatel.osoba?.adresaUplna,
        },
      })),
    }
  }

  const getSubjektDetail = async () => {
    const organizacia = sessionSubjekt?.subjektId
      ? await getSubjektApi
          .portalGetSubjektSubjekty(sessionSubjekt?.subjektId)
          .then((t) => ({
            ...t.data,
            nazov: t.data?.subjektRef?.nazov,
          }))
      : {}

    return organizacia
  }

  const getStatickeData = async ({ jeOrganizacia }: any) => {
    const statutari = jeOrganizacia ? await getStatutari() : null
    const konecniUzivateliaVyhod = jeOrganizacia
      ? await getKonecniUzivateliaVyhod()
      : null
    const subjektDetail = jeOrganizacia ? await getSubjektDetail() : null

    const organizaciaRef = {
      organizaciaId: subjektDetail?.organizacia?.organizaciaId,
      nazov: subjektDetail?.organizacia?.nazov,
      ico: subjektDetail?.organizacia?.ico,
      dic: subjektDetail?.organizacia?.dic,
      cisloVerzie: subjektDetail?.organizacia.cisloVerzie,
    }

    const organizacia = {
      organizaciaRef: organizaciaRef,
      statutari: statutari,
      konecniUzivateliaVyhod: konecniUzivateliaVyhod,
    }

    return {
      ziadatel: {
        osoba: jeOrganizacia ? null : osoba,
        subjektRef: subjektRef,
        organizacia: jeOrganizacia ? organizacia : null,
      },
    }
  }

  async function handleUpdateDraft(kodIrz: string, values: any) {
    /**
     * Problém pri našej `merge` metóde je ten, že ak chcem nahradiť pole, prazdnym poľom,
     * tak sa to samozrejme neodstráni, ale mergne a tým pádom mi prílohy a dokumenty vždy ostanú
     * Tento problém sa deje len v prípade rozpočtu, pretože nie je naimplementovaný cez Vee-Validate
     * ale zvlášť cez piniu.
     * Preto rozpočet pri update vyberieme a použijeme štandardný `deepmerge`, ktorý nahradí staré polia príloh/dokumentov
     * za nové.
     */
    const rozpocet = {
      projekt: {
        rozpocet: {
          polozky: roz.polozky,
          celkovaZiadanaSumaPM: roz.celkovaZiadanaSumaPM,
          celkoveOpravneneVydavky: roz.celkoveOpravneneVydavky,
          ineZdrojeFinancovania: roz.ineZdrojeFinancovania,
          podielFinancovaniaPMPerc: roz.podielFinancovaniaPMPerc,
          vlastneZdrojeZiadatela: roz.vlastneZdrojeZiadatela,
          statnyRozpocet: roz.statnyRozpocetSumar,
        },
      },
    }

    const oldRozpocet = values?.projekt?.rozpocet
    delete values?.projekt?.rozpocet?.polozky
    const mergedRozpocet = deepmerge({ projekt: { rozpocet: oldRozpocet } }, rozpocet)

    const mergedValues = merge(values, mergedRozpocet)

    /**
     * Prílohy sa na backend nesmú odoslať ak nemajú žiadne dokumenty
     * Potrebujeme prefiltrovať dve miesta `data.prilohy` a `data.podmienkyPoskytnutiaPrispevku`
     */
    const prilohy = mergedValues?.prilohy?.filter(
      (priloha: ZoPPMPriloha) =>
        priloha?.priloha?.dokumenty && priloha?.priloha?.dokumenty?.length > 0
    )

    delete mergedValues?.prilohy

    const mergedPrilohy = deepmerge(mergedValues, { prilohy: prilohy })

    /**
     * Rovnako to platí pre prílohy v data.podmienkyPoskytnutiaPrispevku
     * premapujeme každú podmienku, pozrieme sa, či existujú prílohy a v nich dokumenty
     * a potom všetko prefiltrujeme, poukladáme naspať a mergneme so zvyšnými dátami
     */
    const podmienkyPoskytnutiaPrispevku =
      mergedPrilohy?.podmienkyPoskytnutiaPrispevku?.map((podmienka: ZoPPMPodmienka) => {
        const prilohyArr = podmienka?.prilohy
          ?.map((priloha: ZoPPMPriloha) => {
            return priloha?.priloha?.dokumenty && priloha?.priloha?.dokumenty?.length > 0
              ? priloha
              : undefined
          })
          .filter(Boolean)

        const filteredPrilohy = {
          ...(podmienka?.prilohy && prilohyArr && prilohyArr?.length > 0
            ? {
                prilohy: prilohyArr,
              }
            : {}),
        }

        /**
         * Musíme zmazať všetky prílohy, a domergovať prefiltrované prílohy,
         * aby neostávali nezmazané fragmenty v payloade na backend
         */
        delete podmienka?.prilohy

        return {
          ...podmienka,
          ...filteredPrilohy,
        }
      })

    delete mergedPrilohy?.podmienkyPoskytnutiaPrispevku

    const mergedPodmienkyPoskytnutiaPrispevku = deepmerge(mergedPrilohy, {
      /**
       * Podmienky P.P. musíme prefiltrovať a odstrániť `null` hodnoty.
       * Deje sa to pri vypnutí niektorej zo sekcií na ISe - validačný mechanizmus toto vypnutie neberie
       * do úvahy a hodnoty sa ukladajú fixne na svoju pozíciu vo výslednom poli hodnôt ktoré posielame na BE.
       * Pre vypnuté sekcie sa dopĺňajú `null`s, spätne sa však správne dosadia do príslušnej podmienky.
       */
      podmienkyPoskytnutiaPrispevku: Array.isArray(podmienkyPoskytnutiaPrispevku)
        ? podmienkyPoskytnutiaPrispevku.filter(Boolean)
        : podmienkyPoskytnutiaPrispevku,
    })

    const response = await getZoppmApi
      .updateZoppmDraftZoppmDraft(
        kodIrz,
        ZoppmSchemeTransformer.toBackendScheme(mergedPodmienkyPoskytnutiaPrispevku)
      )
      .then((res) => {
        const parsedScheme = ZoppmSchemeTransformer.parseFromBackendScheme(res.data.data!)

        /**
         * Update Vee-Validate useForm() kontextu
         */
        setValues?.(parsedScheme)

        return res
      })
    return response
  }

  return {
    getStatickeData,
    getSubjektDetail,
    getStatutari,
    getKonecniUzivateliaVyhod,
    handleUpdateDraft,
  }
}
